import { useEffect, useState } from 'react'

export const DEFAULT_VIEWER_HEIGHT = 'calc(100vh - 220px)'

const ViewerShell = ({ children, height }) => (
  <div
    className="rounded border overflow-hidden"
    style={{
      borderColor: '#e5e7eb',
      height,
      position: 'relative',
      background: '#f3f4f6',
    }}
  >
    {children}
  </div>
)

const ViewerOverlay = ({ children }) => (
  <div
    className="d-flex flex-column align-items-center justify-content-center gap-3 position-absolute w-100 h-100"
    style={{ zIndex: 2, top: 0, left: 0, background: '#f3f4f6' }}
  >
    {children}
  </div>
)

const PdfViewer = ({ pdfUrl, height }) => {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const timer = setTimeout(() => setLoading(false), 4000)
    return () => clearTimeout(timer)
  }, [pdfUrl])

  const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`

  return (
    <ViewerShell height={height}>
      {loading && (
        <ViewerOverlay>
          <div className="spinner-border spinner-border-sm text-secondary" role="status" />
          <span style={{ fontSize: 13, color: '#9ca3af' }}>Cargando documento…</span>
        </ViewerOverlay>
      )}
      <iframe
        key={pdfUrl}
        src={viewerUrl}
        title="Vista previa del contrato"
        width="100%"
        height="100%"
        style={{ border: 'none', display: 'block' }}
        onLoad={() => setLoading(false)}
      />
    </ViewerShell>
  )
}

const DocxViewer = ({ docxUrl, height }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    setLoading(true)
    setError(false)
    const timer = setTimeout(() => setLoading(false), 4000)
    return () => clearTimeout(timer)
  }, [docxUrl])

  const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docxUrl)}`

  return (
    <ViewerShell height={height}>
      {loading && (
        <ViewerOverlay>
          <div className="spinner-border spinner-border-sm text-secondary" role="status" />
          <span style={{ fontSize: 13, color: '#9ca3af' }}>Cargando documento…</span>
        </ViewerOverlay>
      )}
      {error && (
        <ViewerOverlay>
          <span style={{ fontSize: 13, color: '#ef4444' }}>No se pudo cargar la vista previa.</span>
          <a
            href={docxUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary"
          >
            Descargar documento
          </a>
        </ViewerOverlay>
      )}
      <iframe
        key={docxUrl}
        src={viewerUrl}
        title="Vista previa del contrato (borrador)"
        width="100%"
        height="100%"
        style={{ border: 'none', display: 'block' }}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false)
          setError(true)
        }}
      />
    </ViewerShell>
  )
}

export const ContractDocumentViewer = ({
  finalPdfUrl,
  initialDocxUrl,
  height = DEFAULT_VIEWER_HEIGHT,
  previewLabel = 'contrato',
}) => {
  if (!finalPdfUrl && !initialDocxUrl) {
    return (
      <div
        className="d-flex align-items-center justify-content-center rounded border"
        style={{ height, borderColor: '#e5e7eb', background: '#f9fafb' }}
      >
        <p style={{ fontSize: 14, color: '#9ca3af' }}>No hay documento disponible</p>
      </div>
    )
  }

  if (finalPdfUrl) {
    return (
      <div>
        <div
          className="d-flex align-items-center gap-2 mb-2 px-3 pt-2"
          style={{ fontSize: 12, color: '#6b7280' }}
        >
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth="2">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <span>
            Vista previa del {previewLabel} <strong style={{ color: '#111827' }}>PDF</strong>
          </span>
        </div>
        <PdfViewer pdfUrl={finalPdfUrl} height={height} />
      </div>
    )
  }

  return (
    <div>
      <div
        className="d-flex align-items-center gap-2 mb-2 px-3 pt-2 flex-wrap"
        style={{ fontSize: 12, color: '#6b7280' }}
      >
        <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#2563eb" strokeWidth="2">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
        <span>
          Vista previa del borrador <strong style={{ color: '#111827' }}>DOCX</strong>
        </span>
        <span
          className="rounded-pill px-2"
          style={{
            background: '#fef9c3',
            color: '#92400e',
            fontSize: 11,
            fontWeight: 600,
            border: '1px solid #fde68a',
          }}
        >
          PDF final pendiente de generación
        </span>
      </div>
      <DocxViewer docxUrl={initialDocxUrl} height={height} />
    </div>
  )
}

export const ContractDocumentNav = ({ total, current, onChange }) => {
  if (total <= 1) return null

  return (
    <div className="d-flex align-items-center justify-content-between mb-3 px-1">
      <button
        type="button"
        className="btn btn-sm btn-white border d-flex align-items-center gap-1"
        style={{ borderRadius: 8, fontSize: 13 }}
        onClick={() => onChange(current - 1)}
        disabled={current === 0}
      >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Anterior
      </button>

      <div className="d-flex align-items-center gap-2">
        {Array.from({ length: total }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i)}
            style={{
              width: i === current ? 24 : 8,
              height: 8,
              borderRadius: 4,
              border: 'none',
              padding: 0,
              background: i === current ? '#01A76A' : '#d1d5db',
              transition: 'all 0.25s',
              cursor: 'pointer',
            }}
          />
        ))}
        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 4 }}>
          {current + 1} / {total}
        </span>
      </div>

      <button
        type="button"
        className="btn btn-sm btn-white border d-flex align-items-center gap-1"
        style={{ borderRadius: 8, fontSize: 13 }}
        onClick={() => onChange(current + 1)}
        disabled={current === total - 1}
      >
        Siguiente
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  )
}

export default ContractDocumentViewer
